import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Clock, Eye, AlertCircle } from 'lucide-react';
import { Table, Space, Select } from 'antd';
import PermissionDeniedModal from '../../../components/Modals/PermissionDeniedModal';
import InitializeJobModal from '../Modals/InitializeJobModal';
import LogViewerModal from '../../../components/Modals/jenkinsLogs'; // Import the new component
import { apiClient } from '../../../utils/apiClient';
import { useSelector } from 'react-redux';

const { Option } = Select;

const InitiateJob = ({ canTriggerJob, canScheduleJob}) => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showInitModal, setShowInitModal] = useState(false);
  const [selectedBuildParams, setSelectedBuildParams] = useState(null);
  const [buildCount, setBuildCount] = useState(10);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const user = useSelector(state => state.auth.user);

  useEffect(() => {
    fetchJobs();
    return () => {
      // Clean up any polling when component unmounts
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [buildCount]);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`https://cloudify-prod.wizr.in/job-runner/builds?count=${buildCount}`);
      const data = await response.json();
      setJobs(data.builds);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const startPolling = () => {
    // Clear any existing polling interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    // Set up new polling interval (every 1 second)
    const interval = setInterval(() => {
      fetchJobs();
    }, 1000);
    
    setPollingInterval(interval);
    
    // Auto-stop polling after 1 minute
    setTimeout(() => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }, 60000); // 1 minute timeout
  };

  const handleInitializeJob = async (jobData) => {
    if (!canTriggerJob) {
      setPermissionDenied(true);
      return;
    }

    setIsLoading(true);
    setButtonDisabled(true);
    
    try {
      const response = await fetch('https://cloudify-prod.wizr.in/trigger-build/job-runner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user?.userId,
          parameters: {
            GITHUB_REPO: jobData.gitHubRepo,
            BRANCH_TO_USE: jobData.branchToUse,
            JOB_NAME: jobData.jobName,
            NO_OF_TASKS: jobData.noOfTasks,
            CONTAINER_PARAMS: jobData.containerParams,
            JOB_TYPE: jobData.jobType,
            CRON_EXPRESSION: jobData.cronExpression
          }
        })
      });
      
      const data = await response.json();

      if (response.ok) {
        // Start polling for build updates
        startPolling();
        
        // Re-enable button after 5 seconds
        setTimeout(() => {
          setButtonDisabled(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error triggering build:', error);
      setButtonDisabled(false);
    } finally {
      setIsLoading(true); // Keep loading indicator while polling
      
      // This setTimeout is just to ensure we don't reset isLoading too early
      setTimeout(() => {
        if (!pollingInterval) {
          setIsLoading(false);
        }
      }, 500);
    }
  };

  const getBuildParameters = async (jobNumber) => {
    try {
      // New API endpoint to fetch build parameters
      const response = await fetch(`https://cloudify-prod.wizr.in/job-runner/${jobNumber}/parameters`);
      const data = await response.json();
      setSelectedBuildParams(data.parameters);
      setShowInitModal(true);
    } catch (error) {
      console.error('Error fetching build parameters:', error);
    }
  };

  const viewBuildLogs = (build) => {
    setSelectedBuild(build);
  };

  const columns = [
    {
      title: 'Build #',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: 'Started By',
      dataIndex: 'triggered_by',
      key: 'triggered_by',
      render: (user) => (
        <div className="flex items-center">
          <span className="text-gray-700">{user || 'Unknown'}</span>
        </div>
      ),
    },
    {
      title: 'Build Parameters',
      key: 'parameters',
      render: (_, record) => (
        <button
          onClick={() => getBuildParameters(record.number)}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-all duration-200 shadow-sm text-sm font-medium"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          View & Reuse
        </button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        // Enhanced status badge styling similar to Deployment.js
        const statusConfig = {
          SUCCESS: {
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            borderColor: 'border-green-200',
            icon: '✓'
          },
          FAILURE: {
            bgColor: 'bg-red-100',
            textColor: 'text-red-800', 
            borderColor: 'border-red-200',
            icon: '✗'
          },
          RUNNING: {
            bgColor: 'bg-blue-100',
            textColor: 'text-blue-800',
            borderColor: 'border-blue-200',
            icon: '⟳'
          },
          default: {
            bgColor: 'bg-yellow-100',
            textColor: 'text-yellow-800',
            borderColor: 'border-yellow-200',
            icon: '⟳'
          }
        };
        
        const config = statusConfig[status] || statusConfig.default;
        
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor} border ${config.borderColor} flex items-center justify-center w-24`}>
            <span className="mr-1">{config.icon}</span> {status}
          </span>
        );
      },
    },
    {
      title: 'Started At',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => (
        <div className="flex items-center text-gray-700">
          <Clock className="h-4 w-4 mr-2 text-gray-500" />
          {new Date(timestamp).toLocaleString()}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <button
            onClick={() => viewBuildLogs(record)}
            className="inline-flex items-center px-3 py-1.5 border border-indigo-500 text-indigo-600 bg-white rounded-md hover:bg-indigo-50 transition-all duration-200 shadow-sm text-sm font-medium"
          >
            <Eye className="h-4 w-4 mr-1.5 text-indigo-600" />
            View Logs
          </button>
        </Space>
      ),
    },
  ];


  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Jenkins Jobs History</h2>
          <p className="text-gray-500 text-sm">Recent Job Builds</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-2">Show builds:</span>
            <Select 
              defaultValue={10}
              onChange={(value) => setBuildCount(value)}
              className="w-24"
            >
              <Option value={5}>5</Option>
              <Option value={10}>10</Option>
              <Option value={20}>20</Option>
              <Option value={50}>50</Option>
            </Select>
          </div>
          <button
            onClick={() => {
              setSelectedBuildParams(null);
              setShowInitModal(true);
            }}
            disabled={isLoading || buttonDisabled}
            className="inline-flex items-center text-sm px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 text-white rounded-lg transition-all duration-200 font-medium shadow-md disabled:opacity-70 disabled:cursor-not-allowed gap-2"
          >
            <Plus className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Initializing...' : 'Initialize Job'}
          </button>
        </div>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <Table
          columns={columns}
          dataSource={jobs}
          loading={isLoading}
          rowKey="number"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} jobs`,
          }}
        />
      </div>
      
      {/* Initialize Job Modal */}
      <InitializeJobModal
        isOpen={showInitModal}
        onClose={() => {
          setShowInitModal(false);
          setSelectedBuildParams(null);
        }}
        onSubmit={handleInitializeJob}
        prefillData={selectedBuildParams}
        canTriggerJob = {canTriggerJob}
        canScheduleJob = {canScheduleJob}

      />
      
      {/* Log Viewer Modal - Uses our new reusable component */}
      <LogViewerModal
        isOpen={!!selectedBuild}
        onClose={() => setSelectedBuild(null)}
        logSource={selectedBuild ? `https://cloudify-prod.wizr.in/job-runner/build-logs?build_number=${selectedBuild.number}` : ''}
        title={selectedBuild ? `Build #${selectedBuild?.number} Logs`: ''}
        buildNumber={selectedBuild?.number}
        timestamp={selectedBuild?.timestamp}
        status={selectedBuild?.status}
      />

      <PermissionDeniedModal
        visible={permissionDenied}
        onClose={() => setPermissionDenied(false)}
        message="You don't have permission to trigger batch jobs. Please contact your administrator if you believe this is a mistake."
      />
    </div>
  );
};

export default InitiateJob;